import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  linkedSignal,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import moment from 'moment-timezone';
import { map } from 'rxjs';
import { TimezoneInputComponent } from '../../../../../shared/components/timezone-input/timezone-input.component';
import { DATE_INPUT_FORMAT } from '../../../utils/input-formats';
import { AvailableSchedulePeriod } from '../../../models/schedule-calendar.models';
import { toAvailableSchedulePeriods } from '../../../utils/schedule-timezone.utils';
import { ScheduleTimesFieldsComponent } from '../schedule-times-fields/schedule-times-fields.component';
import {
  createTimesControls,
  ITimesControls,
  RangeFormGroup,
  toAvailableTimes,
} from '../schedule-times-fields/schedule-times-fields.form';
import {
  enumerateSelectedDates,
  parseDate,
} from '../../../utils/schedule-dates.utils';
import {
  CrossFieldErrorStateMatcher,
  dateRangeOrderValidator,
  notAfterValidator,
  notAllInPastValidator,
  notBeforeTodayValidator,
  selectedDaysInRangeValidator,
  timesNotSkippedValidator,
  validDateValidator,
  visibleDaysRequiredValidator,
} from '../../../validators/schedule-calendar.validators';

type AddAvailableTimesForm = FormGroup<
  {
    date: RangeFormGroup;
    days: FormControl<number[]>;
  } & ITimesControls
>;

/**
 * Adds available times over a range of dates, repeating on the selected days
 * of the week.
 */
@Component({
  selector: 'app-schedule-add-available-times-form',
  templateUrl: './schedule-add-available-times-form.component.html',
  styleUrls: ['./schedule-add-available-times-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatButtonModule,
    MatIconModule,
    ScheduleTimesFieldsComponent,
    TimezoneInputComponent,
  ],
})
export class ScheduleAddAvailableTimesFormComponent {
  readonly allowWeekends = input(false);
  readonly disabled = input(false);

  /** The timezone the dates and times start in, e.g. the calendar's */
  readonly defaultTimezoneName = input<string>();

  /**
   * The dates and times are in this timezone, which resets to the default
   * whenever that changes. Today and now are in the browser's timezone while
   * it's invalid.
   */
  readonly timezoneName = linkedSignal(this.defaultTimezoneName);

  private readonly timezoneInput = viewChild.required(TimezoneInputComponent);
  private readonly daysOfWeekLabels = [
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat',
    'Sun',
  ];
  readonly daysOfWeek = computed(() =>
    this.allowWeekends()
      ? this.daysOfWeekLabels
      : this.daysOfWeekLabels.slice(0, 5)
  );
  private readonly defaultDaysSelection = Array.from({ length: 5 }).map(
    (_, i) => i
  );

  readonly minDate = computed(() => this.now().format(DATE_INPUT_FORMAT));
  readonly maxDate = computed(() =>
    this.now().add(1, 'year').format(DATE_INPUT_FORMAT)
  );
  private readonly dateValidators = [
    Validators.required,
    validDateValidator(),
    notBeforeTodayValidator(() => this.minDate()),
    notAfterValidator(() => this.maxDate()),
  ];

  private readonly defaultSubmitLabel = 'Add time';

  private readonly toVisibleDays = (days: number[]) =>
    days.filter((day) => day < this.daysOfWeek().length);

  readonly form: AddAvailableTimesForm = new FormGroup(
    {
      date: new FormGroup(
        {
          start: new FormControl(
            this.defaultDateRange().start,
            this.dateValidators
          ),
          end: new FormControl(
            this.defaultDateRange().end,
            this.dateValidators
          ),
        },
        dateRangeOrderValidator()
      ),
      days: new FormControl(
        this.defaultDaysSelection,
        visibleDaysRequiredValidator(this.toVisibleDays)
      ),
      ...createTimesControls({
        time: { start: '09:00', end: '17:00' },
        allPreferred: false,
        preferredTimes: [],
      }),
    },
    [
      selectedDaysInRangeValidator(this.toVisibleDays),
      notAllInPastValidator(this.toVisibleDays, () => this.now()),
      timesNotSkippedValidator(
        (form) => this.toSelectedDates(form as AddAvailableTimesForm),
        () => this.timezoneName() ?? ''
      ),
    ]
  );

  readonly rangeErrorStateMatcher = new CrossFieldErrorStateMatcher(
    'endBeforeStart'
  );

  private readonly dateRange = toSignal(
    this.form.controls.date.valueChanges.pipe(
      map(() => this.form.controls.date.getRawValue())
    ),
    { initialValue: this.form.controls.date.getRawValue() }
  );
  private readonly selectedDays = toSignal(
    this.form.controls.days.valueChanges,
    { initialValue: this.form.controls.days.value }
  );

  readonly selectedDates = computed(() => {
    const { start, end } = this.dateRange();
    const days = this.toVisibleDays(this.selectedDays());

    if (this.form.controls.date.invalid) {
      return [];
    }

    return enumerateSelectedDates(start, end, days);
  });

  readonly submitLabel = computed(() => {
    const totalSelectedDays = this.selectedDates().length;

    return totalSelectedDays > 1
      ? `Add to ${totalSelectedDays} days`
      : this.defaultSubmitLabel;
  });

  readonly addTimes = output<AvailableSchedulePeriod[]>();

  constructor() {
    effect(() => {
      this.minDate();

      untracked(() => {
        if (this.date.pristine) {
          this.date.setValue(this.defaultDateRange());
          return;
        }

        Object.values(this.date.controls).forEach((control) =>
          control.updateValueAndValidity()
        );
      });
    });

    effect(() => {
      this.daysOfWeek();

      untracked(() => this.form.controls.days.updateValueAndValidity());
    });
  }

  get date() {
    return this.form.controls.date;
  }

  get time() {
    return this.form.controls.time;
  }

  selectAllWeekdays() {
    this.form.controls.days.setValue(this.defaultDaysSelection);
  }

  submit() {
    this.form.updateValueAndValidity();
    this.timezoneInput().touch();

    if (this.form.invalid || !this.timezoneInput().isValid()) {
      this.form.markAllAsTouched();
      return;
    }

    const times = toAvailableTimes(this.form.getRawValue());
    const timezoneName = this.timezoneName();

    this.addTimes.emit(
      this.selectedDates().flatMap((date) =>
        toAvailableSchedulePeriods(
          date.format(DATE_INPUT_FORMAT),
          times,
          timezoneName
        )
      )
    );
  }

  getDateRangeError() {
    const { start, end } = this.date.controls;

    if (!this.date.touched) {
      return null;
    }

    const hasError = (error: string) =>
      start.hasError(error) || end.hasError(error);

    if (hasError('required')) {
      return 'Required';
    }

    if (hasError('invalidDate')) {
      return 'Enter a valid date';
    }

    if (this.date.hasError('endBeforeStart')) {
      return 'End date must be on or after the start date';
    }

    if (hasError('past')) {
      return 'Cannot be in the past';
    }

    return hasError('tooLate') ? 'Cannot be more than a year ahead' : null;
  }

  getDaysError() {
    const { days } = this.form.controls;

    if ((days.dirty || days.touched) && days.hasError('required')) {
      return 'At least one day of the week must be selected';
    }

    const isInteracted = days.dirty || days.touched || this.date.touched;

    return isInteracted && this.form.hasError('noSelectedDaysInRange')
      ? 'None of the selected days fall within the date range'
      : null;
  }

  getPastTimesError() {
    const { days } = this.form.controls;
    const isInteracted =
      this.date.touched || this.time.touched || days.dirty || days.touched;

    return isInteracted && this.form.hasError('allInPast')
      ? 'All selected times are in the past'
      : null;
  }

  getSkippedTimesError() {
    return this.time.touched && this.form.hasError('timesSkipped')
      ? 'These times are skipped when the clocks go forward'
      : null;
  }

  /** In `DATE_INPUT_FORMAT`, read from the form as it's validated */
  private toSelectedDates(form: AddAvailableTimesForm) {
    const { date, days } = form.getRawValue();

    return form.controls.date.invalid
      ? []
      : enumerateSelectedDates(
          date.start,
          date.end,
          this.toVisibleDays(days)
        ).map((selected) => selected.format(DATE_INPUT_FORMAT));
  }

  private now() {
    const timezoneName = this.timezoneName();

    return moment.tz.zone(timezoneName) ? moment.tz(timezoneName) : moment();
  }

  private defaultDateRange() {
    const today = this.minDate();

    return {
      start: today,
      end: parseDate(today).add(6, 'days').format(DATE_INPUT_FORMAT),
    };
  }
}
